import React, { useState } from "react";
import { X } from "lucide-react";
import { motion } from "framer-motion";
import { Alumni } from "../../types/types";
import { usePostAuth } from "../../hooks/usePostAuth";
import {
  modalBackdropVariants,
  modalContentVariants,
} from "../../animations/animation";

interface UpdateAlumniModalProps {
  alumni: Alumni;
  onClose: () => void;
  onSuccess: () => void;
}

const UpdateAlumniModal: React.FC<UpdateAlumniModalProps> = ({
  alumni,
  onClose,
  onSuccess,
}) => {
  const [form, setForm] = useState({
    name: alumni.name ?? "",
    email: alumni.email ?? "",
    phone: alumni.phone ?? "",
    company: alumni.company ?? "",
    bio: alumni.bio ?? "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const { mutate: updateAlumni, isPending: loading } = usePostAuth(
    `/users/${alumni.id}`,
    { queryKeyToInvalidate: ["get-alumnis"] }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const payload: Partial<typeof form> & { _method?: "PATCH" } = {};
    if (form.name !== alumni.name) payload.name = form.name;
    if (form.email !== alumni.email) payload.email = form.email;
    if (form.phone !== alumni.phone) payload.phone = form.phone;
    if (form.company !== alumni.company) payload.company = form.company;
    if (form.bio !== alumni.bio) payload.bio = form.bio;

    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }

    payload._method = "PATCH";

    updateAlumni(payload, {
      onSuccess: () => {
        onSuccess();
        onClose();
      },
      onError: () => {
        alert("Failed to update alumni");
      },
    });
  };

  return (
    <motion.div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      variants={modalBackdropVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      onClick={onClose}
    >
      <motion.div
        className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
        variants={modalContentVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-900">Update Alumni</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name
            </label>
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              name="phone"
              value={form.phone}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company
            </label>
            <input
              name="company"
              value={form.company}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bio
            </label>
            <textarea
              name="bio"
              rows={3}
              value={form.bio}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-medium transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white rounded-md font-medium transition"
            >
              {loading ? "Saving..." : "Update"}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default UpdateAlumniModal;
